import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import PhotoModal from './PhotoModal';
import photoService from '../../services/photoService';
import contractService from '../../services/contractService'; // 🔧 AJOUT: Import pour invalidation cache
import { Camera, Upload, X, Star, StarOff, Edit3, Save, XCircle } from 'lucide-react';

const JewelPhotos = ({ 
  contractId, 
  photos = [], 
  onPhotoAdded, 
  onPhotoDeleted, 
  onPhotoPrimarySet,
  onPhotoDescriptionUpdated,
  className = '' 
}) => {
  const { currentUser: user } = useAuth();
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editingDescription, setEditingDescription] = useState(null);
  const [tempDescription, setTempDescription] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [deleting, setDeleting] = useState(null); // 🔧 AJOUT: State pour gérer les suppressions
  const dragCounter = useRef(0);
  const fileInputRef = useRef(null);

  // Vérifier les permissions d'admin
  const canManagePhotos = user && (user.role === 'admin' || user.role === 'admin_assistant');
  
  console.log('🔍 Debug permissions photos:', {
    user: user,
    userRole: user?.role,
    canManagePhotos: canManagePhotos
  });

  // Photo principale (première avec isPrimary ou première de la liste)
  const primaryPhoto = photos.find(p => p.isPrimary) || photos[0];
  const otherPhotos = photos.filter(p => p !== primaryPhoto);

  // Gestion du drag & drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (canManagePhotos) {
      setDragOver(true);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    dragCounter.current = 0;

    if (!canManagePhotos) return;

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      handleFileUpload(imageFile);
    } else {
      alert('Veuillez déposer une image');
    }
  };

  // 🔧 CORRECTION: Fonction d'upload améliorée avec mise à jour du cache
  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validation avec le service
    const validation = photoService.validateImageFile(file);
    if (!validation.isValid) {
      alert(validation.errors.join('\n'));
      return;
    }

    setUploading(true);

    try {
      console.log('📤 Début upload photo:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        contractId
      });

      const result = await photoService.uploadPhoto(
        contractId, 
        file, 
        '', // description vide pour l'instant
        photos.length === 0 // isPrimary si c'est la première photo
      );

      if (result.success) {
        // 🔧 AJOUT: Invalider le cache du contrat
        contractService.invalidateContractCache(contractId);
        
        onPhotoAdded && onPhotoAdded(result.data);
        console.log('✅ Photo uploadée avec succès:', result.data);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Erreur upload photo:', error);
      alert('Erreur lors de l\'upload de la photo: ' + error.message);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Gérer l'upload de photo depuis l'input
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      await handleFileUpload(file);
    }
  };

  // 🔧 CORRECTION: Supprimer une photo avec gestion d'état améliorée
  const handleDeletePhoto = async (photoId) => {
    if (deleting === photoId) return; // Éviter les double-clics

    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette photo ?')) {
      return;
    }

    setDeleting(photoId); // Marquer comme en cours de suppression

    try {
      console.log('🗑️ Suppression photo:', photoId);
      
      const result = await photoService.deletePhoto(contractId, photoId);

      if (result.success) {
        // 🔧 AJOUT: Invalider le cache du contrat
        contractService.invalidateContractCache(contractId);
        
        onPhotoDeleted && onPhotoDeleted(photoId);
        console.log('✅ Photo supprimée avec succès');
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Erreur suppression photo:', error);
      alert('Erreur lors de la suppression de la photo: ' + error.message);
    } finally {
      setDeleting(null); // Réinitialiser l'état
    }
  };

  // 🔧 CORRECTION: Définir comme photo principale avec mise à jour optimisée
  const handleSetPrimary = async (photoId) => {
    try {
      console.log('⭐ Définition photo principale:', photoId);
      
      const result = await photoService.setPrimaryPhoto(contractId, photoId);

      if (result.success) {
        // 🔧 AJOUT: Invalider le cache du contrat
        contractService.invalidateContractCache(contractId);
        
        onPhotoPrimarySet && onPhotoPrimarySet(photoId);
        console.log('✅ Photo principale définie avec succès');
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Erreur définition photo principale:', error);
      alert('Erreur lors de la définition de la photo principale: ' + error.message);
    }
  };

  // Commencer l'édition de description
  const startEditingDescription = (photo) => {
    setEditingDescription(photo._id);
    setTempDescription(photo.description || '');
  };

  // 🔧 CORRECTION: Sauvegarder la description avec invalidation cache
  const saveDescription = async (photoId) => {
    try {
      console.log('📝 Mise à jour description:', { photoId, description: tempDescription });
      
      const result = await photoService.updatePhotoDescription(contractId, photoId, tempDescription);

      if (result.success) {
        // 🔧 AJOUT: Invalider le cache du contrat
        contractService.invalidateContractCache(contractId);
        
        onPhotoDescriptionUpdated && onPhotoDescriptionUpdated(photoId, tempDescription);
        setEditingDescription(null);
        setTempDescription('');
        console.log('✅ Description mise à jour avec succès');
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Erreur mise à jour description:', error);
      alert('Erreur lors de la mise à jour de la description: ' + error.message);
    }
  };

  // Annuler l'édition
  const cancelEditing = () => {
    setEditingDescription(null);
    setTempDescription('');
  };

  // 🔧 CORRECTION: Gestionnaire de double-clic optimisé
  const handleImageDoubleClick = (photo, e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('🔍 Double-clic sur photo:', photo._id);
    setSelectedPhoto(photo);
  };

  return (
    <div 
      className={`relative bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${className} ${
        dragOver && canManagePhotos ? 'border-blue-400 bg-blue-50' : ''
      }`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Camera className="h-5 w-5 mr-2 text-blue-500" />
          Photos du bijou
          {canManagePhotos && (
            <span className="ml-2 text-xs text-gray-500">
              (Admin: glissez-déposez ou cliquez pour ajouter)
            </span>
          )}
        </h3>
        
        {canManagePhotos && (
          <div className="flex items-center space-x-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />
            <button
              onClick={() => {
                console.log('🔘 Bouton upload cliqué, input ref:', fileInputRef.current);
                fileInputRef.current?.click();
              }}
              disabled={uploading}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Upload...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Ajouter
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Zone de drag & drop visible */}
      {canManagePhotos && dragOver && (
        <div className="absolute inset-0 border-2 border-dashed border-blue-400 bg-blue-50 bg-opacity-75 flex items-center justify-center z-10 rounded-lg pointer-events-none">
          <div className="text-center">
            <Upload className="mx-auto h-12 w-12 text-blue-400" />
            <p className="mt-2 text-lg font-medium text-blue-600">Déposez votre image ici</p>
          </div>
        </div>
      )}

      {photos.length === 0 ? (
        <div 
          className={`text-center py-8 rounded-lg border-2 border-dashed transition-colors ${
            canManagePhotos 
              ? 'border-gray-300 hover:border-blue-400 hover:bg-blue-50 cursor-pointer' 
              : 'border-gray-200'
          }`}
          onClick={() => canManagePhotos && fileInputRef.current?.click()}
        >
          <Camera className="mx-auto h-12 w-12 text-gray-300" />
          <p className="mt-2 text-sm text-gray-500">Aucune photo disponible</p>
          {canManagePhotos && (
            <p className="text-xs text-gray-400 mt-1">
              Cliquez ici ou glissez-déposez une image pour ajouter la première photo
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Photo principale */}
          {primaryPhoto && (
            <div className="relative group">
              {/* 🔧 CORRECTION: Conteneur d'image avec aspect ratio fixe et responsive */}
              <div className="relative w-full h-64 sm:h-80 lg:h-96 bg-gray-100 rounded-lg overflow-hidden">
                <img
                  src={primaryPhoto.url}
                  alt={primaryPhoto.description || 'Photo du bijou'}
                  className="absolute inset-0 w-full h-full object-contain cursor-pointer hover:opacity-95 transition-opacity"
                  onDoubleClick={(e) => handleImageDoubleClick(primaryPhoto, e)}
                  loading="lazy"
                />
                
                {/* Badge photo principale */}
                <div className="absolute top-2 left-2 z-10">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <Star className="h-3 w-3 mr-1" />
                    Principale
                  </span>
                </div>

                {/* Actions admin */}
                {canManagePhotos && (
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <div className="flex space-x-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePhoto(primaryPhoto._id);
                        }}
                        disabled={deleting === primaryPhoto._id}
                        className="p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Supprimer cette photo"
                      >
                        {deleting === primaryPhoto._id ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Overlay avec info double-clic */}
                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all duration-200 flex items-end justify-center pb-2 pointer-events-none">
                  <span className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-50 px-2 py-1 rounded">
                    Double-cliquez pour agrandir
                  </span>
                </div>
              </div>

              {/* Description de la photo principale */}
              <div className="mt-2">
                {editingDescription === primaryPhoto._id ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={tempDescription}
                      onChange={(e) => setTempDescription(e.target.value)}
                      placeholder="Description de la photo"
                      className="flex-1 text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      maxLength={100}
                    />
                    <button
                      onClick={() => saveDescription(primaryPhoto._id)}
                      className="p-1 text-green-600 hover:text-green-700"
                      title="Sauvegarder"
                    >
                      <Save className="h-4 w-4" />
                    </button>
                    <button
                      onClick={cancelEditing}
                      className="p-1 text-gray-600 hover:text-gray-700"
                      title="Annuler"
                    >
                      <XCircle className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600 italic">
                      {primaryPhoto.description || 'Aucune description'}
                    </p>
                    {canManagePhotos && (
                      <button
                        onClick={() => startEditingDescription(primaryPhoto)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Modifier la description"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Autres photos en miniatures */}
          {otherPhotos.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Autres photos ({otherPhotos.length})
              </h4>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {otherPhotos.map((photo) => (
                  <div key={photo._id} className="relative group">
                    {/* 🔧 CORRECTION: Conteneur miniature avec aspect ratio fixe */}
                    <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={photo.url}
                        alt={photo.description || 'Photo du bijou'}
                        className="w-full h-full object-cover cursor-pointer hover:opacity-95 transition-opacity"
                        onDoubleClick={(e) => handleImageDoubleClick(photo, e)}
                        loading="lazy"
                      />

                      {/* Actions admin pour miniatures */}
                      {canManagePhotos && (
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-20 flex items-center justify-center space-x-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetPrimary(photo._id);
                            }}
                            className="p-1 bg-yellow-600 text-white rounded-full hover:bg-yellow-700 transition-colors"
                            title="Définir comme photo principale"
                          >
                            <StarOff className="h-3 w-3" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePhoto(photo._id);
                            }}
                            disabled={deleting === photo._id}
                            className="p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Supprimer cette photo"
                          >
                            {deleting === photo._id ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                            ) : (
                              <X className="h-3 w-3" />
                            )}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Description en miniature */}
                    {photo.description && (
                      <p className="text-xs text-gray-500 mt-1 truncate" title={photo.description}>
                        {photo.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal d'agrandissement */}
      {selectedPhoto && (
        <PhotoModal
          photo={selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
          photos={photos}
          currentIndex={photos.findIndex(p => p._id === selectedPhoto._id)}
          onNavigate={(index) => setSelectedPhoto(photos[index])}
        />
      )}
    </div>
  );
};

export default JewelPhotos;